import { usePermissions, FunctionField, TopToolbar, useTranslate, TextField, SelectInput } from 'react-admin';
import { useTheme, useMediaQuery } from '@mui/material';
import { SearchBar } from '@/components/global/list/searchBar.jsx';
import CustomList from '@/components/global/list/CustomList.jsx';
import { CustomDatagridConfigurable } from '@/components/global/list/CustomDatagridConfigurable.jsx';
import ListCollectionPointField from '@/components/collectionPoint/ListCollectionPointField.jsx';
import { PeriodFilter, ReferenceArrayFilter } from '@/components/global/filters/filters.jsx';
import { useMemo } from 'react';
import FromToFilterComponent from '@/components/global/filters/FromToFilterComponent.jsx';
import { StatusField } from '@/components/bag/StatusField.jsx';
import { SealField } from '@/components/bag/SealField.jsx';
import { WeightField } from '@/components/bag/WeightField.jsx';
import { ContainerCountField } from '@/components/bag/ContainerCountField.jsx';
import { TransactionCountField } from '@/components/bag/TransactionCountField.jsx';
import { useDrawer } from '@/composables/useDrawer';
import { SearchFilterInput } from '@/components/global/filters/SearchFilterInput';
import { PeriodFilterInput } from '@/components/global/filters/PeriodFilterInput.jsx';
import CustomFilterButton from '@/components/global/filters/CustomFilterButton';
import { CustomSelectColumnsButton } from '@/components/global/buttons/CustomSelectColumnsButton.jsx';
import CustomToolbarActionsPopover from '@/components/global/buttons/CustomToolbarActionsPopover';
import CustomExportButton from '@/components/global/buttons/CustomExportButton.jsx';
import ColumnsIcon from '@/assets/icons/columns-icon.svg?react';
import { FillingTimeField } from '@/components/bag/FillingTimeField.jsx';
import { BinLevelField } from '@/components/bag/BinLevelField.jsx';
import { bagStatusChoices, operatorFilterChoices } from '@/components/global/options/selectOptions.jsx';
import CustomSelectArrayInput from '@/components/global/inputs/CustomSelectArrayInput';
import { ChangeSealButton, CloseManuallyButton, ResendButton } from '@/components/bag/buttons/actionButtons.jsx';
import CachedDataActionButton from '@/components/global/buttons/CachedDataActionButton.jsx';
import { BulkActionsContainer } from '@/components/global/buttons/bulkButtons.jsx';

const ListActions = () => {
    const translate = useTranslate();
    const { permissions } = usePermissions();

    return (
        <TopToolbar>
            <SearchFilterInput />
            <PeriodFilterInput label={translate('resources.bag.filters.period')} />
            <CustomFilterButton />
            <CustomSelectColumnsButton startIcon={<ColumnsIcon />} style={{ margin: '0px -4px' }} />
            <CustomToolbarActionsPopover>
                <CustomExportButton label={translate('common.exportCsv')} />
                {permissions.isAdmin && <CachedDataActionButton resource='bag' type={null} />}
            </CustomToolbarActionsPopover>
        </TopToolbar>
    );
};

export const BagList = () => {
    const { isLoading, permissions } = usePermissions();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const { openDrawer } = useDrawer();
    const translate = useTranslate();
    const filters = useMemo(
        () => [
            <SearchBar key='search' source='value' label={translate('common.search')} alwaysOn />,
            <PeriodFilter key='period' alwaysOn={true} />,
            <ReferenceArrayFilter
                key='collectionPoint->contractor->id_in'
                source='collectionPoint->contractor->id_in'
                label={translate('common.contractor')}
                optionText={choice => choice.name}
                reference='contractor'
                searchFields={['name']}
                forceDesktopOnMobile={true}
                alwaysOn={isMobile}
            />,
            <CustomSelectArrayInput
                key='collectionPoint->operator_in'
                source='collectionPoint->operator_in'
                label={translate('common.operator')}
                choices={operatorFilterChoices(translate)}
                forceDesktopOnMobile={true}
                alwaysOn={isMobile}
                fullWidth
                alwaysDropdown={true}
            />,
            <ReferenceArrayFilter
                key='collectionPoint->id_in'
                source='collectionPoint->id_in'
                label={translate('common.collectionPoint')}
                optionText={choice => choice.customId}
                reference='collection-point'
                searchFields={['customId']}
                forceDesktopOnMobile={true}
                alwaysOn={isMobile}
            />,
            <ReferenceArrayFilter
                key='city'
                source='collectionPoint->address->city_in'
                reference='city'
                label={translate('common.city')}
                forceDesktopOnMobile={true}
                alwaysOn={isMobile}
            />,
            <FromToFilterComponent
                key='weight'
                source='weight_range'
                label={translate('resources.bag.fields.weight')}
                forceDesktopOnMobile={true}
                alwaysOn={isMobile}
            />,
            <ReferenceArrayFilter
                key='groupId_in'
                source='device->groupId_in'
                reference='device-group'
                label={translate('common.deviceGroup')}
                forceDesktopOnMobile={true}
                alwaysOn={isMobile}
            />,
            <FromToFilterComponent
                key='sealScanningTime_time'
                source='sealScanningTime_time_range'
                label={translate('common.filters.hours')}
                type='time'
                forceDesktopOnMobile={true}
                alwaysOn={isMobile}
            />,
            <SelectInput
                key='status'
                source='status'
                label={translate('common.status')}
                choices={bagStatusChoices(translate)}
                emptyText={translate('common.all')}
            />,
        ],
        [isMobile, translate],
    );

    if (isLoading) return null;

    return (
        <CustomList actions={<ListActions />} filters={filters}>
            <CustomDatagridConfigurable bulkActionButtons={permissions.isAdmin ? <BulkActionButtons /> : false}>
                <FunctionField source='status' label={translate('common.status')} render={record => <StatusField record={record} />} />
                <FunctionField source='seal' label={translate('resources.bag.fields.seal')} sortBy='sealScanningTime' render={SealField} />
                <FunctionField
                    source='collectionPoint.id'
                    label={translate('common.collectionPoint')}
                    sortBy='collectionPoint.customId'
                    render={record => (
                        <ListCollectionPointField
                            collectionPoint={record.collectionPoint}
                            onClick={() => openDrawer(record.collectionPointId, 'collection-point-show')}
                        />
                    )}
                />
                <FunctionField
                    source='device.serialNumber'
                    label={translate('common.deviceSerialNumberShort')}
                    sortBy='device.serialNumber'
                    render={record => (
                        <TextField
                            source='device.serialNumber'
                            sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                            onClick={e => {
                                e.stopPropagation();
                                if (record.device && record.device.id) {
                                    openDrawer(record.device.id, 'device-show');
                                }
                            }}
                        />
                    )}
                />
                <FunctionField
                    source='fillingTime'
                    label={translate('resources.bag.fields.fillingTime')}
                    render={record => <FillingTimeField record={record} />}
                />
                <FunctionField source='binLevel' label={translate('resources.bag.fields.binLevel')} render={BinLevelField} />
                <FunctionField source='containerCount' label={translate('resources.bag.fields.containerCount')} render={ContainerCountField} />
                <FunctionField source='transactionCount' label={translate('common.transactionsCount')} render={TransactionCountField} />
                <FunctionField source='weight' label={translate('resources.bag.fields.weight')} render={WeightField} />
            </CustomDatagridConfigurable>
        </CustomList>
    );
};

const BulkActionButtons = () => (
    <BulkActionsContainer>
        <ResendButton />
        <CloseManuallyButton />
        <ChangeSealButton />
    </BulkActionsContainer>
);
